/**
 * The pieces every place on the page is made of, as prototype/talk draws them: the left side's rows,
 * a place's head, a line, a poll, the call card, an announcement, a file's tile and the composer.
 */
import { type ReactNode, useRef, useState } from "react";
import { Face, FaceStack } from "../../components/ui/Face";
import { Icon, type IconName } from "../../components/ui/Icon";
import { api } from "../../lib/api";
import type { GuestNextLive, TalkAnnouncement, TalkFace, TalkFile, TalkMessage, TalkWho } from "../../../shared/talk";
import type { Attachment } from "../../../shared/types";
import { postWhen } from "../../talk/when";
import { startedWords } from "../series";
import { fileType } from "./Bubbles";
import { usePage } from "./context";
import { fileHref } from "./model";
import { seatHref, useCalendarMenu } from "./Pinned";
import { callWords, joinedWords, relDay, timeOf, votedWords } from "./talk";
import type { Chat } from "./useFeed";

export const faceOf = (w: TalkWho | TalkFace): { name: string; photo: string | null } => ({ name: w.name, photo: w.photoUrl });

// --- the left side ---

export function Item({ icon, label, count, live, on, onClick }: { icon: IconName; label: string; count?: number; live?: boolean; on?: boolean; onClick?: () => void }): ReactNode {
  return (
    <button type="button" className={on ? "jp-it on" : "jp-it"} onClick={onClick}>
      <Icon name={icon} />
      <span className="jp-it-n">{label}</span>
      {live ? <span className="jp-livetag">Live</span> : count ? <i className="jp-n">{count}</i> : null}
    </button>
  );
}

/** The call happening now, on the left side while the person is anywhere but What's new. */
export function LiveCard(): ReactNode {
  const { guest, host, nowMs } = usePage();
  const live = guest.liveNow;
  if (!live) return null;
  return (
    <div className="jp-livecard">
      <span className="jp-lc-top">
        <span className="jp-pulse">
          <i />
          Live
        </span>
        <small>{startedWords(live.startedAt, nowMs)}</small>
      </span>
      <b>{live.circleId ? `${host.first} is live with your circle` : `${host.first} is live`}</b>
      {live.joined.length ? (
        <span className="jp-lc-who">
          <FaceStack className="fs" people={live.joined.map(faceOf)} size={26} />
          <small>{joinedWords(live.joined.length)}</small>
        </span>
      ) : null}
      <a className="jp-btn light" href={live.joinPath}>
        <Icon name="live" /> Join now
      </a>
    </div>
  );
}

// --- a place's head ---

/** What is the person's own, from their face: their photo, what they are told about, and their answers. */
function useYouMenu(): (anchor: HTMLElement) => void {
  const { openMenu, openSheet } = usePage();
  return (anchor) =>
    openMenu({
      anchor: anchor.getBoundingClientRect(),
      items: [
        { key: "photo", icon: <Icon name="pic" />, label: "Your photo", run: () => openSheet("photo") },
        { key: "tell", icon: <Icon name="bell" />, label: "Tell me when", run: () => openSheet("tell") },
        { key: "answers", icon: <Icon name="note" />, label: "Your answers", run: () => openSheet("answers") },
      ],
    });
}

/** The person's own chip: their face and first name. */
export function YouChip(): ReactNode {
  const { me } = usePage();
  const open = useYouMenu();
  return (
    <button type="button" className="jp-chip" aria-haspopup="menu" data-menu-anchor onClick={(e) => open(e.currentTarget)}>
      <Face name={me.name} photo={me.avatar} size={26} />
      {me.first}
    </button>
  );
}

/** The person's face at a phone's top corner: the same menu as the chip. */
export function MeButton({ size }: { size: number }): ReactNode {
  const { me } = usePage();
  const open = useYouMenu();
  return (
    <button type="button" className="gq-me" aria-haspopup="menu" aria-label={me.first} data-menu-anchor onClick={(e) => open(e.currentTarget)}>
      <Face name={me.name} photo={me.avatar} size={size} />
    </button>
  );
}

/** The head of a place on a computer or an iPad. An iPad upright has no right column, so the person's chip sits here. */
export function Head({ pic, icon, title, sub, right, big }: { pic?: string | null; icon?: IconName; title: string; sub: ReactNode; right?: ReactNode; big?: boolean }): ReactNode {
  const { lay } = usePage();
  return (
    <header className={big ? "jp-ch big" : "jp-ch"}>
      {pic ? <img className="jp-ch-pic" src={pic} alt="" /> : null}
      {icon ? (
        <span className="jp-ch-ic">
          <Icon name={icon} />
        </span>
      ) : null}
      <span className="jp-ch-t">
        <h2>{title}</h2>
        <small>{sub}</small>
      </span>
      {right}
      {lay === "upright" ? <YouChip /> : null}
    </header>
  );
}

/** The head of a pushed screen on a phone: a round Back, the place, and the person's face. */
export function Nav({ title, sub, center }: { title?: ReactNode; sub?: ReactNode; center?: ReactNode }): ReactNode {
  const { back } = usePage();
  return (
    <header className="gq-nav">
      <button type="button" className="gq-back" aria-label="Back" onClick={back}>
        <Icon name="left" />
      </button>
      {center ?? (
        <span className="gq-nav-t">
          <b>{title}</b>
          {sub ? <small>{sub}</small> : null}
        </span>
      )}
      <MeButton size={32} />
    </header>
  );
}

// --- the right side ---

export function Pins({ children }: { children: ReactNode }): ReactNode {
  return (
    <aside className="jp-pins" aria-label="Pinned">
      <div className="jp-me">
        <YouChip />
      </div>
      {children}
    </aside>
  );
}

const MONTH_LONG = new Intl.DateTimeFormat("en-US", { month: "long" });
const DAY_LONG = new Intl.DateTimeFormat("en-US", { weekday: "long" });

/** "Tuesday 27 October". */
export function longDay(at: number): string {
  const d = new Date(at * 1000);
  return `${DAY_LONG.format(d)} ${d.getDate()} ${MONTH_LONG.format(d)}`;
}

/** The soonest live, with the way to put it in a calendar. */
export function NextLive({ next }: { next: GuestNextLive | null }): ReactNode {
  const { data, token, nowMs, inCalendar } = usePage();
  const calendar = useCalendarMenu();
  if (!next) return null;
  const room = data.sessions.find((s) => s.webinar_id === next.webinarId);
  const when = relDay(next.startsAt, nowMs);
  const open = room?.live ? nowMs >= Date.parse(room.live.opensAt) : false;
  return (
    <div className="jp-next">
      <small>{when ? `Next live · ${when}` : "Next live"}</small>
      <b>{longDay(next.startsAt)}</b>
      <span>
        {timeOf(next.startsAt)}
        {next.registered ? " · You are registered" : ""}
      </span>
      {room && open ? (
        <a className="jp-btn s" href={seatHref(room, token)}>
          <Icon name="live" /> Join the room
        </a>
      ) : room?.calendar ? (
        inCalendar(room.webinar_id) ? (
          <span className="jp-btn soft s">
            <Icon name="check" /> In your calendar
          </span>
        ) : (
          <button type="button" className="jp-btn soft s" data-menu-anchor onClick={(e) => calendar(e.currentTarget, room)}>
            <Icon name="cal" /> Add to my calendar
          </button>
        )
      ) : null}
    </div>
  );
}

/** One switch under "Tell me when" and on a circle's next call. */
export function Switch({ on, label, onChange }: { on: boolean; label: string; onChange: (on: boolean) => void }): ReactNode {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={on ? "jp-tog on" : "jp-tog"} onClick={() => onChange(!on)} />;
}

/** What the person hears about, by email and on the page. */
export function TellMe(): ReactNode {
  const { guest, host, setPrefs } = usePage();
  const p = guest.prefs;
  const rows: [keyof typeof p, string, string][] = [
    ["live", `${host.first} goes live`, "By email, and here"],
    ["answers", "Someone answers me", "By email, and here"],
    ["announcements", `${host.first} posts an announcement`, "By email, and here"],
    ["digest", "Everything new in my rooms", "One email a day"],
  ];
  return (
    <>
      <h3>Tell me when</h3>
      {rows.map(([k, label, sub]) => (
        <div className="jp-set" key={k}>
          <span>
            <b>{label}</b>
            <small>{sub}</small>
          </span>
          <Switch on={p[k]} label={label} onChange={(on) => setPrefs({ [k]: on })} />
        </div>
      ))}
    </>
  );
}

/** A replay, pinned. */
export function ReplayPin({ title, poster, line, label = "Your replay", onClick }: { title: string; poster: string | null; line: string; label?: string; onClick: () => void }): ReactNode {
  return (
    <button type="button" className="jp-pin" onClick={onClick}>
      <span className="jp-pin-pic">
        {poster ? <img src={poster} alt="" /> : null}
        <Icon name="playf" />
      </span>
      <span className="jp-pin-t">
        <small>{label}</small>
        <b>{title}</b>
        <span>{line}</span>
      </span>
    </button>
  );
}

/** Files shown small on the right: a link that opens the file the way Files does. */
export function FileChip({ f }: { f: TalkFile }): ReactNode {
  const open = useOpenFile();
  return (
    <button type="button" className="jp-file" onClick={() => open(f)}>
      <Icon name={f.kind === "link" ? "link" : "file"} /> {f.name}
    </button>
  );
}

/** Opens a file: a picture or video in the viewer, anything else in a new tab; the host sees that it was opened. */
export function useOpenFile(): (f: TalkFile) => void {
  const { token, view } = usePage();
  return (f) => {
    api.guestTalk.opened(token, f.key).catch(() => {});
    if (f.kind === "picture" || f.kind === "video") view({ url: f.url, name: f.name, meta: f.size ?? "", video: f.kind === "video" });
    else window.open(f.url, "_blank", "noopener,noreferrer");
  };
}

// --- a place's lines ---

/** The word a search found, lit in the line that holds it. */
export function Marked({ text, word }: { text: string; word: string }): ReactNode {
  const w = word.trim();
  if (!w) return text;
  const parts = text.split(new RegExp(`(${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "i"));
  return parts.map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>));
}

/** A file's face in a list: paper for a document, a frame for a replay, a picture. */
export function FileTile({ f }: { f: TalkFile }): ReactNode {
  if (f.kind === "video")
    return (
      <span className="ft vid">
        {f.posterUrl ? <img src={f.posterUrl} alt="" /> : null}
        <Icon name="playf" />
      </span>
    );
  if (f.kind === "picture")
    return (
      <span className="ft pic">
        <Icon name="pic" />
      </span>
    );
  return (
    <span className="ft pdf">
      <b>{f.kind === "link" ? "LINK" : f.kind === "pdf" ? "PDF" : fileType(f.name).badge}</b>
    </span>
  );
}

/** A first page, drawn small for a preview of a document. */
export function DocPage({ title, by }: { title: string; by: string }): ReactNode {
  return (
    <div className="doc" aria-hidden="true">
      <b>{title}</b>
      <small>{by}</small>
      {[88, 74, 81, 66, 84, 58, 78].map((w, i) => (
        <i key={i} style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

export function AttachmentChips({ list }: { list: readonly Attachment[] }): ReactNode {
  const { view } = usePage();
  return list.map((a, i) => {
    const href = fileHref(a);
    // Why a card with no link: a file the host shared whose copy is not stored here still shows what it is called, as the host's side does.
    if (!href)
      return (
        <span key={i} className="jp-file">
          <Icon name="file" /> {a.name}
        </span>
      );
    if (a.kind === "image")
      return (
        <button key={i} type="button" className="jp-file" onClick={() => view({ url: href, name: a.name, meta: a.size ?? "", video: false })}>
          <Icon name="pic" /> {a.name}
        </button>
      );
    return (
      <a key={i} className="jp-file" href={href} target="_blank" rel="noopener noreferrer">
        <Icon name="file" /> {a.name}
      </a>
    );
  });
}

/** The poll in a line: one choice, changeable, with how many voted. */
function Poll({ m, vote }: { m: TalkMessage; vote: (m: TalkMessage, choice: number) => void }): ReactNode {
  const poll = m.poll;
  if (!poll) return null;
  return (
    <div className="jp-poll">
      <p>{poll.question}</p>
      {poll.options.map((o, i) => (
        <button key={o.label} type="button" className={poll.mine === i ? "jp-poll-o mine" : "jp-poll-o"} aria-pressed={poll.mine === i} onClick={() => vote(m, i)}>
          <i style={{ width: `${poll.voted > 0 ? Math.round((o.votes / poll.voted) * 100) : 0}%` }} />
          <b>
            {poll.mine === i ? <Icon name="check" /> : null}
            {o.label}
          </b>
          <em>{o.votes}</em>
        </button>
      ))}
      <small>
        {poll.mine !== null ? "You voted · " : ""}
        {votedWords(poll.voted, poll.of)}
      </small>
    </div>
  );
}

/** A call's card: the small line, the call, its day and length, and the way to watch it again. */
export function CallCard({ pics, eyebrow, title, line, action }: { pics: (string | null)[]; eyebrow: string; title: string; line: string; action: ReactNode }): ReactNode {
  const shown = pics.filter((p): p is string => Boolean(p)).slice(0, 4);
  return (
    <div className="jp-call">
      <span className={shown.length < 2 ? "jp-call-pic solo" : "jp-call-pic"}>
        {shown.map((p) => (
          <img key={p} src={p} alt="" />
        ))}
        <Icon name="playf" />
      </span>
      <span className="jp-call-t">
        <small>{eyebrow}</small>
        <b>{title}</b>
        <span>{line}</span>
      </span>
      {action}
    </div>
  );
}

/** One line of Everyone or a circle. */
export function Line({ m, chat, onReply, failed, retry, hostFirst }: { m: TalkMessage; chat: Chat; onReply: (m: TalkMessage) => void; failed: boolean; retry: () => void; hostFirst: string }): ReactNode {
  const { playTalk, lay } = usePage();
  const mine = m.who.kind === "member" && m.who.me === true;
  const host = m.who.kind === "host";
  const f = faceOf(m.who);
  const size = lay === "phone" ? 32 : 36;
  return (
    <div className={mine ? "jp-ln me" : host ? "jp-ln lead" : "jp-ln"} data-id={m.id}>
      <Face name={f.name || "A member"} photo={f.photo} size={size} />
      <div className="jp-ln-b">
        <b>
          {mine ? "You" : f.name || "A member"}
          {host ? <span className="jp-host">Host</span> : null}
          <small>{timeOf(m.createdAt)}</small>
        </b>
        {m.body ? <p>{m.body}</p> : null}
        {m.attachments.length ? <AttachmentChips list={m.attachments} /> : null}
        <Poll m={m} vote={chat.vote} />
        {m.call ? (
          <CallCard
            pics={m.call.faces.map((x) => x.photoUrl)}
            eyebrow="The call's replay"
            title={m.call.title}
            line={`${m.call.minutes} min · ${m.call.came} came${m.call.youCame ? " · you were there" : ""}`}
            action={
              <button type="button" className="jp-btn soft s" onClick={() => playTalk(m.call?.webinarId ?? "", { sessionId: m.call?.liveSessionId, start: true })}>
                Watch again
              </button>
            }
          />
        ) : null}
        <span className="jp-ln-f">
          {!mine ? (
            <button type="button" className={m.hearted ? "jp-react on" : "jp-react"} aria-pressed={m.hearted} aria-label={m.hearted ? "Remove your heart" : "Heart this"} onClick={() => chat.heart(m)}>
              <Icon name="heart" fill={m.hearted} /> {m.hearts > 0 ? m.hearts : null}
            </button>
          ) : m.hearts > 0 ? (
            <span className="jp-react on">
              <Icon name="heart" fill /> {m.hearts}
            </span>
          ) : null}
          {m.replies ? (
            <span className="jp-ln-r">
              {m.replies} {m.replies === 1 ? "reply" : "replies"}
            </span>
          ) : null}
          {failed ? (
            <button type="button" className="jp-ln-r" onClick={retry}>
              Not sent. Tap to try again.
            </button>
          ) : (
            <button type="button" className="jp-ln-r" aria-label={`Reply to ${f.name || hostFirst}`} onClick={() => onReply(m)}>
              Reply
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

export function TypingLine({ name }: { name: string }): ReactNode {
  return (
    <p className="jp-typing">
      <span>
        <i />
        <i />
        <i />
      </span>
      {name ? `${name} is typing` : "Someone is typing"}
    </p>
  );
}

/** The pinned line, once, above the chat. */
export function PinBar({ who, text }: { who: string; text: string }): ReactNode {
  return (
    <div className="jp-pinbar">
      <Icon name="pin" />
      <span>
        <b>{who}:</b> {text}
      </span>
    </div>
  );
}

// --- an announcement ---

/** A post in Announcements: heart it, or reply to the host privately. */
export function PostCard({ p, onHeart, onReply }: { p: TalkAnnouncement; onHeart: () => void; onReply: () => void }): ReactNode {
  const { host, nowMs, playTalk } = usePage();
  return (
    <article className="post" data-id={p.id}>
      <header className="post-h">
        <Face name={host.name} photo={host.avatar} size={36} />
        <span className="post-who">
          <b>{host.name}</b>
          <small>{postWhen(p.createdAt, nowMs)}</small>
        </span>
      </header>
      {p.body ? <p className="post-t">{p.body}</p> : null}
      {p.replay ? (
        <button type="button" className="post-replay" onClick={() => playTalk(p.webinarId, { sessionId: p.replay?.liveSessionId, start: true })}>
          <span className="post-replay-pic">
            {p.replay.posterUrl ? <img src={p.replay.posterUrl} alt="" /> : null}
            <Icon name="playf" />
          </span>
          <span className="post-replay-t">
            <small>The replay</small>
            <b>{p.replay.title}</b>
            <span>{callWords(p.replay.startsAt, p.replay.minutes)}</span>
          </span>
        </button>
      ) : null}
      {p.attachments.length ? <AttachmentChips list={p.attachments} /> : null}
      <footer className="post-f">
        <button type="button" className={p.hearted ? "post-heart on" : "post-heart"} aria-pressed={p.hearted} aria-label={p.hearted ? "Remove your heart" : "Heart this"} onClick={onHeart}>
          <Icon name="heart" fill={p.hearted} /> {p.hearts}
        </button>
        <button type="button" className="post-reply" onClick={onReply}>
          <Icon name="chat" /> Reply to {host.first}
        </button>
      </footer>
    </article>
  );
}

// --- the composer of a room or a circle ---

const FILE_MAX = 20 * 1024 * 1024;
const FILES_MAX = 5;
const ACCEPT = "image/*,.pdf,.doc,.docx,.rtf,.odt,.xls,.xlsx,.csv,.ods,.txt,.md";

export interface Quote {
  who: string;
  text: string;
}

/** Words, up to five files, and Send; a reply shows what it answers. */
export function PlaceComposer({
  placeholder,
  send,
  noteTyping,
  quote,
  clearQuote,
}: {
  placeholder: string;
  send: (body: string, attachments: Attachment[]) => void;
  noteTyping: () => void;
  quote: Quote | null;
  clearQuote: () => void;
}): ReactNode {
  const { token, toast, lay } = usePage();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const go = async () => {
    const body = text.trim();
    if ((!body && !files.length) || busy) return;
    setBusy(true);
    try {
      const attachments: Attachment[] = [];
      for (const f of files) {
        const asset = await api.watch.upload(token, f);
        attachments.push({ kind: asset.kind === "image" ? "image" : "file", name: asset.name, size: asset.size, key: asset.key });
      }
      send(body, attachments);
      setText("");
      setFiles([]);
      clearQuote();
    } catch {
      toast("That did not go up. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const add = (list: FileList | null) => {
    if (!list) return;
    const room = FILES_MAX - files.length;
    const ok = [...list].filter((f) => f.size <= FILE_MAX);
    if (ok.length < list.length) toast("Pictures and files can be 20 MB at most.");
    if (ok.length > room) toast(`Up to ${FILES_MAX} files at a time.`);
    setFiles((have) => [...have, ...ok.slice(0, room)]);
  };
  return (
    <form
      className={lay === "phone" ? "jp-cmp gq-cmp" : "jp-cmp"}
      onSubmit={(e) => {
        e.preventDefault();
        void go();
      }}
    >
      {quote || files.length ? (
        <div className="jp-cmp-top">
          {quote ? (
            <span className="jp-cmp-quote">
              <Icon name="chat" />
              <span>
                Replying to <b>{quote.who}</b>: {quote.text}
              </span>
              <button type="button" aria-label="Cancel reply" onClick={clearQuote}>
                <Icon name="x" />
              </button>
            </span>
          ) : null}
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="jp-file">
              <Icon name="file" /> {f.name}
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((have) => have.filter((_, j) => j !== i))}>
                <Icon name="x" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <button type="button" className="jp-cmp-add" aria-label="Add a picture or file" onClick={() => pick.current?.click()}>
        <Icon name="plus" />
      </button>
      <input
        className="jp-cmp-in"
        value={text}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          noteTyping();
        }}
      />
      <button type="submit" className="jp-cmp-go" aria-label="Send" disabled={busy}>
        <Icon name="send" />
      </button>
      <input
        ref={pick}
        type="file"
        multiple
        hidden
        accept={ACCEPT}
        onChange={(e) => {
          add(e.target.files);
          e.target.value = "";
        }}
      />
    </form>
  );
}
